import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { User } from "@rago/web-shared";
import { api } from "../../lib/apiClient";
import { setActiveServerId } from "../../lib/activeServer";
import { auth } from "../../lib/authManager";
import { countdown, expiryFraction, secondsLeft } from "../../lib/countdown";
import { resourcesQuery } from "../../lib/resourcesQuery";
import { useAuth } from "../../lib/useAuth";
import { InitialDisc } from "../../components/approval-card";
import { ArrowUpRightIcon, CheckIcon, CopyIcon } from "../../components/icons";
import { Spinner } from "../../components/ui";

/**
 * The claim code (Plex's claim page; Claude Design 1a "Tidy card"): ONE
 * `rc-` code to copy and nothing to run — the docs carry the commands.
 * Minted on arrival (api GET /v1/claim/token: four minutes, single use); the
 * server takes it in its /setup "Have a claim code" field, `rago-server
 * claim --token`, RAGO_CLAIM_TOKEN or install.sh --token. Under the app bar,
 * centred in what is below it: a header and one card — the code field with
 * Copy inside it, a time-left bar, a status row that holds the waiting state
 * and the countdown, and the account the server will be added to (Kenny,
 * 2026-10-09). The page keeps watching the account's servers: the first new
 * one is the claim, and the page becomes "Server connected".
 */
export const Route = createFileRoute("/_authed/claim")({
  component: Claim,
});

interface ClaimToken {
  claimToken: string;
  expiresAt: string;
  /** When this page received it — the bar's full width. The API sends only
   * the deadline. */
  issuedAt: string;
}

type View =
  | { kind: "minting" }
  | { kind: "code"; token: ClaimToken }
  | { kind: "error"; error: unknown };

/** idle → copied (2 s) → again, for the rest of the code's life; failed = the
 * clipboard refused, the code is selected instead. */
type CopyState = "idle" | "copied" | "again" | "failed";

const COPIED_MS = 2000;
/** The last stretch of a code's life: the bar and the time turn red (Kenny:
 * red, not the warn yellow), and the live region says so once. */
const ENDING_SOON_S = 30;
const DOCS_URL = "https://docs.rago.tv";

/** .rago-page clears the bar; min-h 100dvh + justify-center puts the column
 * at the centre of what is below it. */
const ROOT =
  "rago-page flex min-h-[100dvh] flex-col items-center justify-center px-6 pb-16 text-center max-sm:px-4";
const HEADING =
  "rago-display text-[34px] leading-[1.1] tracking-[-0.02em] text-[var(--rago-text-1)] max-sm:text-[28px]";

// .rago-btn (app.css) is unlayered: its padding, `font: inherit` and glow
// beat any utility, so the pills that must differ from it are sized inline.
/** The pill inside the code field: no glow on the dark inset, the mock's
 * padding. The 46px height is a utility (.rago-btn sets none). */
const FIELD_PILL: CSSProperties = { boxShadow: "none", padding: "0 18px 0 16px", fontSize: "14.5px" };
const NEW_CODE_PILL: CSSProperties = { boxShadow: "none", padding: "0 20px", fontSize: "14.5px" };
/** The account row's small secondary pill. */
const SMALL_PILL: CSSProperties = { boxShadow: "none", padding: "0 14px", fontSize: "13px" };
/** The connected block's two pills. */
const BIG_PILL: CSSProperties = { padding: "0 32px", fontSize: "15px" };

function Claim() {
  const navigate = useNavigate();
  const state = useAuth();
  const user = state.status === "signedIn" ? state.user : null;
  const [view, setView] = useState<View>({ kind: "minting" });
  const [now, setNow] = useState(() => Date.now());
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const copiedTimer = useRef<number | null>(null);
  const [announce, setAnnounce] = useState("");
  const warned = useRef(false);
  const codeRef = useRef<HTMLSpanElement>(null);
  const newCodeRef = useRef<HTMLButtonElement>(null);
  const addLibraryRef = useRef<HTMLButtonElement>(null);
  const [claimedServer, setClaimedServer] = useState<{ id: string; name: string } | null>(null);
  const knownServers = useRef<Set<string> | null>(null);

  const resources = useQuery({
    ...resourcesQuery,
    // Poll fast until the claimed server can actually be reached — candidates
    // appear only once its HTTPS cert is issued, ~10-15 s after the claim —
    // and keep polling from a background tab, and past the code's expiry: the
    // user is off on the server's setup page while this page waits, and a
    // code can read 0:00 while the claim it was spent on is still landing.
    refetchInterval: (query) => {
      if (!claimedServer) return 5_000;
      const claimed = query.state.data?.items.find((s) => s.serverId === claimedServer.id);
      return (claimed?.connections?.length ?? 0) > 0 ? false : 5_000;
    },
    refetchIntervalInBackground: true,
  });

  // Snapshot the servers present when the page mounts; a new one = the claim.
  useEffect(() => {
    if (knownServers.current || !resources.data) return;
    knownServers.current = new Set(resources.data.items.map((s) => s.serverId));
  }, [resources.data]);

  useEffect(() => {
    if (claimedServer || !resources.data || !knownServers.current) return;
    const fresh = resources.data.items.find((s) => !knownServers.current!.has(s.serverId));
    if (fresh) {
      setClaimedServer({ id: fresh.serverId, name: fresh.name });
      // You just connected it — you're in it now (top-bar chip + rail follow).
      setActiveServerId(fresh.serverId);
    }
  }, [resources.data, claimedServer]);

  async function mint() {
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    setView({ kind: "minting" });
    setCopyState("idle");
    setAnnounce("");
    warned.current = false;
    try {
      const token = await api<Omit<ClaimToken, "issuedAt">>("/v1/claim/token");
      setView({ kind: "code", token: { ...token, issuedAt: new Date().toISOString() } });
    } catch (error) {
      setView({ kind: "error", error });
    }
  }

  // A code on arrival, once per mount: a dev double-run would mint two and
  // leave the first one unused.
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    void mint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The clock the countdown reads. The deadline is the token's, not the
  // tick's: a hidden tab's timers are throttled, so a return to the tab reads
  // the clock at once, and the ticking stops with the code.
  const expiresAt = view.kind === "code" ? view.token.expiresAt : null;
  useEffect(() => {
    if (!expiresAt) return;
    setNow(Date.now());
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (secondsLeft(expiresAt, t) === 0) window.clearInterval(id);
    }, 1000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") setNow(Date.now());
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [expiresAt]);

  useEffect(
    () => () => {
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  const token = view.kind === "code" ? view.token.claimToken : null;
  const left = expiresAt ? secondsLeft(expiresAt, now) : 0;
  const expired = view.kind === "code" && left === 0;
  const live = view.kind === "code" && !expired;
  const endingSoon = live && left <= ENDING_SOON_S;
  const fraction = view.kind === "code" ? expiryFraction(view.token, now) : 0;

  // The live region speaks once per change of state, never per tick.
  useEffect(() => {
    if (endingSoon && !warned.current) {
      warned.current = true;
      setAnnounce("30 seconds left");
    }
  }, [endingSoon]);
  useEffect(() => {
    if (!expired) return;
    setAnnounce("This code expired");
    newCodeRef.current?.focus({ preventScroll: true });
  }, [expired]);
  useEffect(() => {
    if (!claimedServer) return;
    setAnnounce("Server connected");
    addLibraryRef.current?.focus({ preventScroll: true });
  }, [claimedServer]);

  /** The clipboard refused: leave the code selected, so Ctrl/Cmd+C takes it. */
  function selectCode() {
    const el = codeRef.current;
    const selection = window.getSelection();
    if (!el || !selection) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  // "Copied" only when the clipboard took it, then "Copy again" for the rest
  // of the code's life.
  function copy() {
    if (!token) return;
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    const took = () => {
      setCopyState("copied");
      setAnnounce("Copied");
      copiedTimer.current = window.setTimeout(() => setCopyState("again"), COPIED_MS);
    };
    const refused = () => {
      setCopyState("failed");
      selectCode();
      setAnnounce("Couldn't copy. The code is selected, copy it.");
    };
    const clipboard = navigator.clipboard;
    if (!clipboard) {
      refused();
      return;
    }
    clipboard.writeText(token).then(took, refused);
  }

  // The claim landed: the token is spent, whatever the clock says.
  if (claimedServer) {
    return (
      <div className={ROOT}>
        <ConnectedBlock
          name={claimedServer.name}
          primaryRef={addLibraryRef}
          // The Libraries page itself, not its Add dialog: the user presses
          // Add library there (Kenny, 2026-10-09). `setup=1` would open it.
          onAddLibrary={() =>
            void navigate({
              to: "/settings/server/$serverId/libraries",
              params: { serverId: claimedServer.id },
              search: {},
            })
          }
        />
        <span role="status" className="sr-only">
          {announce}
        </span>
      </div>
    );
  }

  return (
    <div className={ROOT}>
      <div className="w-full max-w-[560px]">
        <h1 className={`rago-rise ${HEADING}`}>Connect your server</h1>
        <p className="rago-rise rago-rise-1 mx-auto mt-3 max-w-[470px] text-[16px] leading-[1.55] text-[var(--rago-text-2)] [text-wrap:pretty]">
          Copy this code and paste it where your server asks for a claim code. It works once.{" "}
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 font-semibold whitespace-nowrap text-[var(--rago-text-1)] transition-colors hover:text-[var(--rago-accent-strong)]"
          >
            Setup guide
            <ArrowUpRightIcon size={13} />
          </a>
        </p>

        {/* The auth card's surface (app.css .rago-auth-card), 24px: the
            backdrop is blurred already, so no blur of its own. */}
        <div className="rago-rise rago-rise-2 mt-[30px] overflow-hidden rounded-[24px] border border-[rgba(255,255,255,0.08)] bg-[rgba(20,20,24,0.62)] text-left shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
          <div className="px-6 pt-[22px] pb-6 max-sm:px-4 max-sm:pt-4 max-sm:pb-4">
            <div className="rago-caps mb-3">Claim code</div>
            <CodeField
              view={view}
              token={token}
              expired={expired}
              live={live}
              copyState={copyState}
              codeRef={codeRef}
              newCodeRef={newCodeRef}
              onCopy={copy}
              onMint={() => void mint()}
            />
          </div>
          <TimeBar token={token} live={live} fraction={fraction} warn={endingSoon} />
          <StatusRow view={view} live={live} expired={expired} endingSoon={endingSoon} left={left} />
          {user ? <AccountRow user={user} /> : null}
        </div>
        <span role="status" className="sr-only">
          {announce}
        </span>
      </div>
    </div>
  );
}

/** The code, selectable whole (one click takes it all, a copy from any
 * selection takes it unbroken), with the Copy pill inside the field. Under
 * 640px the field is a column: the code on top, the pill full width. */
function CodeField(props: {
  view: View;
  token: string | null;
  expired: boolean;
  live: boolean;
  copyState: CopyState;
  codeRef: RefObject<HTMLSpanElement | null>;
  newCodeRef: RefObject<HTMLButtonElement | null>;
  onCopy: () => void;
  onMint: () => void;
}) {
  const { view, token, expired, copyState } = props;
  const pill = "rago-btn h-[46px] max-sm:w-full";

  let button: ReactNode;
  if (view.kind === "error") {
    button = (
      <button type="button" className={pill} style={NEW_CODE_PILL} onClick={props.onMint}>
        Try again
      </button>
    );
  } else if (view.kind === "minting") {
    button = (
      <button type="button" className={pill} style={FIELD_PILL} disabled>
        <CopyIcon size={16} />
        Copy code
      </button>
    );
  } else if (expired) {
    button = (
      <button
        ref={props.newCodeRef}
        type="button"
        className={pill}
        style={NEW_CODE_PILL}
        onClick={props.onMint}
      >
        Get a new code
      </button>
    );
  } else if (copyState === "copied") {
    button = (
      <button type="button" className={pill} style={FIELD_PILL} onClick={props.onCopy}>
        <CheckIcon size={16} bold />
        Copied
      </button>
    );
  } else if (copyState === "again") {
    button = (
      <button type="button" className={`${pill} rago-btn-quiet`} style={FIELD_PILL} onClick={props.onCopy}>
        <CopyIcon size={16} />
        Copy again
      </button>
    );
  } else {
    button = (
      <button type="button" className={pill} style={FIELD_PILL} onClick={props.onCopy}>
        <CopyIcon size={16} />
        Copy code
      </button>
    );
  }

  return (
    <div className="flex h-[66px] items-center gap-3 rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[rgba(0,0,0,0.32)] pl-5 pr-2.5 max-sm:h-auto max-sm:flex-col max-sm:items-stretch max-sm:gap-3 max-sm:p-3">
      <span
        ref={props.codeRef}
        onCopy={
          token
            ? (e) => {
                e.clipboardData.setData("text/plain", token);
                e.preventDefault();
              }
            : undefined
        }
        className={`min-w-0 flex-1 truncate font-mono text-[21px] font-medium whitespace-nowrap select-all max-sm:text-center max-sm:text-[clamp(15px,4.6vw,19px)] ${
          expired
            ? "text-[var(--rago-text-dim)] line-through decoration-[rgba(255,255,255,0.35)]"
            : "text-[var(--rago-text-1)]"
        }`}
      >
        {view.kind === "minting" ? (
          <span className="inline-flex align-middle text-[var(--rago-accent)]">
            <Spinner size={16} />
          </span>
        ) : null}
        {/* The whole code, prefix included, in one colour: it is all the
            code (Kenny). */}
        {token}
      </span>
      <div className="shrink-0 max-sm:w-full">{button}</div>
    </div>
  );
}

/** The code's life, left to right. Keyed by the code, so a fresh one paints
 * full instead of growing from the old width. */
function TimeBar(props: { token: string | null; live: boolean; fraction: number; warn: boolean }) {
  return (
    <div aria-hidden className="h-[2px] w-full bg-[var(--rago-line-1)]">
      {props.live ? (
        <div
          key={props.token ?? ""}
          className={`h-full transition-[width] duration-1000 ease-linear ${
            props.warn ? "bg-[var(--rago-error)]" : "bg-[var(--rago-accent)]"
          }`}
          style={{ width: `${Math.round(props.fraction * 1000) / 10}%` }}
        />
      ) : null}
    </div>
  );
}

/** What the page is doing, and how long the code has. The time ticks, so
 * this row is not a live region (the page's sr-only one speaks). */
function StatusRow(props: { view: View; live: boolean; expired: boolean; endingSoon: boolean; left: number }) {
  const { view, live, expired, endingSoon, left } = props;
  const spin = (
    <span className="inline-flex shrink-0 text-[var(--rago-accent)]">
      <Spinner size={16} />
    </span>
  );
  const dot = <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--rago-error)]" />;
  let lead: ReactNode;
  if (view.kind === "error") lead = <>{dot}Couldn't get a code</>;
  else if (view.kind === "minting") lead = <>{spin}Getting a code</>;
  else if (expired) lead = <>{dot}This code expired</>;
  else lead = <>{spin}Waiting for your server</>;
  return (
    <div className="flex h-[52px] items-center justify-between gap-4 bg-[rgba(255,255,255,0.02)] px-6 text-[14px] max-sm:px-4">
      <div className="flex min-w-0 items-center gap-2.5 font-medium text-[var(--rago-text-2)]">{lead}</div>
      {live ? (
        <div className="shrink-0 text-[13px] text-[var(--rago-text-3)]">
          Expires in{" "}
          <span
            className={`font-semibold tabular-nums ${
              endingSoon ? "text-[var(--rago-error)]" : "text-[var(--rago-text-1)]"
            }`}
          >
            {countdown(left)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Whose server it becomes. Signing out lands on sign-in with the way back
 * here (routes/_authed), and the next account gets its own code. */
function AccountRow({ user }: { user: User }) {
  return (
    <div className="flex items-center gap-3.5 border-t border-[rgba(255,255,255,0.06)] py-4 pr-[18px] pl-6 max-sm:flex-wrap max-sm:px-4">
      <InitialDisc name={user.displayName} size={36} />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="text-[12.5px] text-[var(--rago-text-3)]">Server will be added to</div>
        <div className="mt-0.5 truncate text-[14.5px]">
          <span className="font-semibold text-[var(--rago-text-1)]">{user.displayName}</span>
          <span className="text-[var(--rago-text-3)]"> · {user.email}</span>
        </div>
      </div>
      {/* On a phone the pill takes its own line under the text, aligned with
          it (disc + gap), instead of squeezing the email. */}
      <div className="shrink-0 max-sm:ml-[50px] max-sm:basis-full">
        <button
          type="button"
          className="rago-btn rago-btn-quiet h-9"
          style={SMALL_PILL}
          onClick={() => void auth.signOut()}
        >
          Use another account
        </button>
      </div>
    </div>
  );
}

/** The claim landed. Add a library is the next step for a server claimed
 * from the installer or an env var; a wizard claim has its own. */
function ConnectedBlock(props: {
  name: string;
  primaryRef: RefObject<HTMLButtonElement | null>;
  onAddLibrary: () => void;
}) {
  return (
    <div className="flex w-full max-w-[520px] flex-col items-center gap-6">
      <div className="rago-rise flex h-[68px] w-[68px] items-center justify-center rounded-full border border-[rgba(62,207,142,0.32)] bg-[rgba(62,207,142,0.14)] text-[var(--rago-ok)]">
        <CheckIcon size={30} bold />
      </div>
      <h1 className={`rago-rise rago-rise-1 ${HEADING}`}>Server connected</h1>
      <p className="rago-rise rago-rise-2 text-[16px] leading-[1.55] text-[var(--rago-text-2)] [text-wrap:pretty]">
        <span className="font-semibold text-[var(--rago-text-1)] [overflow-wrap:anywhere]">{props.name}</span>{" "}
        is now linked to your account.
      </p>
      <div className="rago-rise rago-rise-3 flex flex-wrap justify-center gap-3">
        <button
          ref={props.primaryRef}
          type="button"
          className="rago-btn h-[50px]"
          style={BIG_PILL}
          onClick={props.onAddLibrary}
        >
          Add a library
        </button>
        <Link to="/" className="rago-btn rago-btn-quiet h-[50px]" style={BIG_PILL}>
          Done
        </Link>
      </div>
    </div>
  );
}
